import { useState, useEffect } from 'react'
import { Icon } from '../ui/icons/icon.tsx'
import { Link, useFetcher } from '@remix-run/react'
import { MainContainer } from './main-container.tsx'
import { cn } from '~/utils/misc.tsx'
import { Button } from '../ui/inputs/button/Button.tsx'
import { Spacer } from '../ui/spacer.tsx'
import { Dialog, DialogHeader } from '../ui/dialog.tsx'
import { Card } from '../ui/card.tsx'
import { TextareaField } from '../ui/inputs/forms.tsx'
import { z } from 'zod'
import { useForm, getInputProps, getFormProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useToast } from '~/features/toast/use-toast.ts'

export const HelpRequestSchema = z.object({
  description: z
    .string()
    .transform(val => val.trim())
    .refine(val => val.length >= 30, {
      message: 'La descripción debe tener al menos 30 caracteres.',
    }),
})

type AppBarProps = {
  onHamburgerMenu: () => void
  userName?: string
  className?: string
  userRole?: string
  userTo?: string
}

const HelpRequestDialog = ({
  helpModalOpen,
  onCloseHelpModal,
}: {
  helpModalOpen: boolean
  onCloseHelpModal: () => void
}) => {
  const [fetcherKey, setFetcherKey] = useState(0)
  const fetcher = useFetcher<any>({ key: `help-request-${fetcherKey}` })
  const { toast } = useToast()
  const isSubmitting = fetcher.state === 'submitting'

  const [form, fields] = useForm({
    id: 'help-request',
    constraint: getZodConstraint(HelpRequestSchema),
    lastResult: fetcher.data?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: HelpRequestSchema })
    },
    shouldRevalidate: 'onBlur',
  })

  const onClose = () => {
    onCloseHelpModal()
    setFetcherKey(prev => prev + 1) // reset fetcher
  }

  // Handle response and show toast notifications
  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      if (fetcher.data.success) {
        // Show success toast
        toast({
          title: 'Solicitud enviada',
          description:
            fetcher.data.message ||
            'Tu solicitud de ayuda ha sido enviada correctamente.',
        })
        // Close modal and reset fetcher
        onClose()
      } else if (fetcher.data.error) {
        // Show error toast
        toast({
          title: 'Error',
          description:
            fetcher.data.error ||
            'Ha ocurrido un error al procesar tu solicitud.',
        })
        // Close modal and reset fetcher
        onClose()
      }
    }
  }, [fetcher.state, fetcher.data, toast, onCloseHelpModal, onClose])

  return (
    <Dialog
      open={helpModalOpen}
      className="Dialog max-w-[600px]"
      onClose={onCloseHelpModal}
    >
      <DialogHeader variant="separator-left">Solicitar Ayuda</DialogHeader>
      <Spacer size="3xs" />
      <Card className="flex flex-col gap-6 shadow-none">
        <div className="text-center">
          Describe el problema que estás experimentando y nuestro equipo te
          ayudará a resolverlo.
        </div>

        <fetcher.Form
          method="POST"
          action="/resources/help-request"
          className="flex flex-col gap-4"
          {...getFormProps(form)}
        >
          <TextareaField
            labelProps={{
              children: 'Describe tu problema:',
              className: 'text-sm font-medium text-gray-700',
            }}
            textareaProps={{
              ...getInputProps(fields.description, { type: 'text' }),
              placeholder:
                'Por favor, describe detalladamente el problema que estás experimentando...',
              rows: 4,
              className: 'w-full',
              disabled: isSubmitting,
            }}
            errors={fields.description.errors}
          />

          <div className="mt-4 flex flex-row justify-center gap-4">
            <Button
              type="submit"
              variant="primary"
              className="min-w-24"
              disabled={!fields.description.value?.trim() || isSubmitting}
            >
              {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
            </Button>
            <Button
              type="button"
              variant="cancel"
              className="min-w-24 bg-secondary text-primary hover:bg-gray-200 hover:text-primary"
              onClick={onCloseHelpModal}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
          </div>
        </fetcher.Form>
      </Card>
    </Dialog>
  )
}

export const AppBar = ({
  onHamburgerMenu,
  userName,
  className,
  userRole,
  userTo,
}: AppBarProps) => {
  const [helpModalOpen, setHelpModalOpen] = useState(false)

  const onCloseHelpModal = () => {
    setHelpModalOpen(false)
  }

  return (
    <>
      <header className="fixed left-0 right-0 top-0 z-[6] bg-white">
        <MainContainer
          className={cn(className, 'flex w-full justify-between pt-4')}
        >
          <Link to="/" className="header__logo">
            <img
              className="h-[30px]"
              src="/img/logo-itraack.svg"
              alt="ITrack logo"
            />
          </Link>
          <div className="inline-flex items-center">
            <div className="hidden items-center gap-3 md:inline-flex">
              <span className="mr-8 font-bold text-primary md:text-3xl">
                Bienvenido {userName},
              </span>
              <Button variant="gray" onClick={() => setHelpModalOpen(true)}>
                Necesitas ayuda?
              </Button>
              {userRole === 'user' ? (
                <Link to={userTo ?? '/employees'}>
                  <Icon
                    name="user"
                    className="h-8 w-8 rounded-full bg-secondary p-1"
                  />
                </Link>
              ) : (
                <Icon
                  name="user"
                  className="h-8 w-8 rounded-full bg-secondary p-1"
                />
              )}
              <form action="/logout" method="POST">
                <button
                  type="submit"
                  className="appearance-none border-none bg-transparent"
                >
                  <Icon className="h-8 w-8 text-primary" name="logout" />
                </button>
              </form>
            </div>
            <button
              className="header__hamburger inline-flex h-8 w-8 cursor-pointer items-center justify-center md:hidden"
              onClick={onHamburgerMenu}
            >
              <Icon
                className="text-body-md inline-flex h-16 w-16"
                name="hamburger-menu"
              />
            </button>
          </div>
        </MainContainer>
        <Spacer size="4xs" />
      </header>

      {/* Help Modal */}
      <HelpRequestDialog
        helpModalOpen={helpModalOpen}
        onCloseHelpModal={onCloseHelpModal}
      />
    </>
  )
}
